import { supabase } from "@/integrations/supabase/client";
import { splitCategorizeItems, type QuestionType } from "@/lib/questionTypes";

export interface TrialQuestion {
  id: string;
  text: string;
  title: string;
  points: number;
  kLevel: string;
  questionType: QuestionType;
  options?: { id: string; text: string }[];
  matchPairs?: { left: string; right: string }[];
  categories?: { name: string; items: string[] }[];
  orderItems?: { id: string; text: string }[];
}

let cached: Promise<TrialQuestion[]> | null = null;

/**
 * Loads the free sample questions (no answer keys) once per page load.
 *
 * The sample exam confirmation modal calls this as soon as it opens, so the
 * exam page has the questions ready and does not flash a loading screen.
 */
export const loadTrialQuestions = (): Promise<TrialQuestion[]> => {
  if (!cached) {
    cached = (async () => {
      const { data, error } = await supabase
        .from("questions_trial")
        .select("*")
        .order("question_id", { ascending: true });
      if (error) {
        cached = null;
        throw error;
      }
      return (data ?? []).map((r: any) => ({
        id: r.question_id,
        text: r.title,
        title: r.title,
        points: r.points ?? 1,
        kLevel: r.k_level,
        questionType: (r.question_type || "single-choice") as QuestionType,
        options: r.options ?? undefined,
        matchPairs: r.match_pairs ?? undefined,
        categories: (r.categories ?? undefined)?.map((c: any) => ({
          name: c.name,
          items: splitCategorizeItems(c.items),
        })),
        orderItems: r.order_items ?? undefined,
      }));
    })();
  }
  return cached;
};

/** Prefetch helper: fire and forget, errors are handled on the exam page. */
export const prefetchTrialQuestions = () => {
  void loadTrialQuestions().catch(() => {});
};
